import { gtag } from '@worldcoo/ui/lib/analytics';

/**
 * Grants Google Consent Mode storage for an embedded (iframe) donation flow.
 *
 * The document always starts denied — `bootstrapHeadScripts` emits the `consent` `default` inline in the
 * head, before gtag.js can read it — because that is the only safe baseline for a visitor who has not
 * answered anything yet. Embedded flows are the one case where the answer is already known: the
 * integrator collected consent on the platform hosting the iframe, so we do not show a banner there (see
 * the `isInIframe` branch in `__root.tsx`) and must supply the decision the absent banner would have.
 *
 * Without this the embed is strictly worse than having no Consent Mode at all: the denied default stands
 * forever because no CMP ever arrives to update it, so gtag.js runs storageless and embedded donations
 * go unmeasured.
 *
 * The same premise decides whether the tag loads at all. `loadGtag` is gated on the CMP's answer for the
 * analytics service, and `useUsercentricsServiceConsent` reports `'granted'` in an embed for exactly the
 * reason given above — so the two halves of the decision stay in step. If the embed premise is ever
 * withdrawn, both have to go, not just this one.
 *
 * Grants `analytics_storage` ONLY, which is exactly what accepting everything on our own banner produces
 * today — verified: Usercentrics answers "Aceptar todo" with `analytics_storage: granted` and all three
 * `ad_*` types still denied, because the configuration registers no advertising services. Mirroring that
 * keeps the embedded and non-embedded paths honest with each other rather than granting an embed more
 * than a donor on the open web can grant. **If an advertising service is ever added to the Usercentrics
 * configuration, this has to be revisited** — it will not start granting ads on its own.
 *
 * Note what is NOT affected: reCAPTCHA, Adyen and every other first-party script load through our own
 * code and are not gated on Consent Mode, so they already work in an embed. Consent Mode governs Google
 * tag storage, nothing else.
 *
 * Queues through {@link gtag}, so ordering does not matter: the command lands in `dataLayer` whether or
 * not gtag.js has arrived, and gtag.js drains the queue when it does.
 */
export const grantEmbeddedConsent = (): void => {
	gtag('consent', 'update', { analytics_storage: 'granted' });
};
